import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, User, Mail, MessageSquare, Tag, CheckCircle2 } from "lucide-react";
import { Button } from "components/Button";

const SupportModal = ({ isOpen, onClose }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        // Simulate API call
        await new Promise((resolve) => setTimeout(resolve, 1500));
        setIsSubmitting(false);
        setIsSuccess(true);
        setTimeout(() => {
            setIsSuccess(false);
            onClose();
            setFormData({ name: "", email: "", subject: "", message: "" });
        }, 2500);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[100] cursor-pointer"
                    />

                    {/* Modal Container */}
                    <div className="fixed inset-0 flex items-center justify-center z-[101] p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            className="w-full max-w-lg bg-background border shadow-2xl rounded-2xl overflow-hidden pointer-events-auto"
                        >
                            {/* Header */}
                            <div className="relative p-6 border-b bg-primary/5">
                                <div className="flex items-center space-x-3">
                                    <div className="p-2 bg-primary/10 rounded-lg">
                                        <MessageSquare className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold">{isSuccess ? "Message Envoyé !" : "Contactez le Support"}</h2>
                                        <p className="text-sm text-muted-foreground">Nous vous répondrons dans les plus brefs délais.</p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="absolute top-4 right-4 p-2 hover:bg-primary/10 rounded-full transition-colors"
                                >
                                    <X className="h-5 w-5 text-muted-foreground" />
                                </button>
                            </div>

                            {/* Form Content */}
                            <div className="p-6">
                                <AnimatePresence mode="wait">
                                    {isSuccess ? (
                                        <motion.div
                                            key="success"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="flex flex-col items-center justify-center py-12 text-center"
                                        >
                                            <CheckCircle2 className="h-16 w-16 text-primary mb-4 animate-bounce" />
                                            <h3 className="text-lg font-semibold mb-2">Merci pour votre message</h3>
                                            <p className="text-muted-foreground">Ahmed de l'équipe support vous répondra prochainement.</p>
                                        </motion.div>
                                    ) : (
                                        <motion.form
                                            key="form"
                                            onSubmit={handleSubmit}
                                            className="space-y-4"
                                        >
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium flex items-center text-muted-foreground">
                                                        <User className="h-3.5 w-3.5 mr-2" />
                                                        Nom Complet
                                                    </label>
                                                    <input
                                                        required
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        placeholder="Jean Dupont"
                                                        className="w-full px-4 py-2 bg-secondary/50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium flex items-center text-muted-foreground">
                                                        <Mail className="h-3.5 w-3.5 mr-2" />
                                                        Email
                                                    </label>
                                                    <input
                                                        required
                                                        type="email"
                                                        name="email"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        placeholder="jean@exemple.com"
                                                        className="w-full px-4 py-2 bg-secondary/50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium flex items-center text-muted-foreground">
                                                    <Tag className="h-3.5 w-3.5 mr-2" />
                                                    Sujet
                                                </label>
                                                <input
                                                    required
                                                    name="subject"
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                    placeholder="Comment pouvons-nous vous aider ?"
                                                    className="w-full px-4 py-2 bg-secondary/50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-sm font-medium flex items-center text-muted-foreground">
                                                    <MessageSquare className="h-3.5 w-3.5 mr-2" />
                                                    Message
                                                </label>
                                                <textarea
                                                    required
                                                    name="message"
                                                    value={formData.message}
                                                    onChange={handleChange}
                                                    rows={4}
                                                    placeholder="Décrivez votre demande en détail..."
                                                    className="w-full px-4 py-2 bg-secondary/50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                                                />
                                            </div>

                                            <Button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl shadow-lg shadow-primary/20 transition-all"
                                            >
                                                {isSubmitting ? (
                                                    <div className="flex items-center space-x-2">
                                                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                        <span>Envoi en cours...</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center space-x-2">
                                                        <Send className="h-4 w-4" />
                                                        <span>Envoyer le Message</span>
                                                    </div>
                                                )}
                                            </Button>
                                        </motion.form>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default SupportModal;
